import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "../styles/Home.css";

import heroImg from "../assets/hero.png";
import stoveImg from "../assets/stove.jpg";
import stove2Img from "../assets/stove2.png";
import europeImg from "../assets/europe.jpg";
import adviceImg from "../assets/advice.png";
import componentsImg from "../assets/components.png";

import pelletImg from "../assets/pellet.jpg";
import firewoodImg from "../assets/firewood.jpg";
import oliveImg from "../assets/olive.jpg";
import biofuelImg from "../assets/biofuel.jpg";

import {
  FaLeaf,
  FaTree,
  FaSeedling,
  FaCertificate,
  FaBolt,
  FaTools,
  FaHeadset,
  FaTruck,
  FaShieldAlt,
} from "react-icons/fa";
import { GiOlive } from "react-icons/gi";
import { Helmet } from "react-helmet-async";

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [topProducts, setTopProducts] = useState([]);

  useEffect(() => {
    document.title = `${t("home.helmetTitle")} - Alfasol`;
    loadTopProducts();
  }, [t]);

  const loadTopProducts = async () => {
    const response = await fetch("/api/products/top-selling", {
      credentials: "include",
    });

    if (response.ok) {
      setTopProducts(await response.json());
    }
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Alfasol",
            url: "https://alfasol.es",
            telephone: "+34641292961",
            email: "raul@alfasol.es",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Calle Albercones, 14",
              postalCode: "18170",
              addressLocality: "Alfacar",
              addressRegion: "Andalucía",
              addressCountry: "ES",
            },
            areaServed: ["España", "Portugal"],
            description:
              "Distribución especializada de componentes técnicos y recambios para estufas y calderas de biomasa.",
          })}
        </script>
      </Helmet>

      <section
        className="hero"
        style={{ backgroundImage: `url(${heroImg})` }}
      >
        <div className="hero-overlay">
          <div className="container hero-content">
            <h1>{t("home.title")}</h1>
            <p>{t("home.subtitle")}</p>

            <p className="eco-badge">
              🌱 {t("home.ecoBadge")}
            </p>

            <button
              className="primary-btn"
              onClick={() => navigate("/shop")}
            >
              {t("home.cta")}
            </button>
          </div>
        </div>
      </section>

      {topProducts.length > 0 && (
        <section className="section best-sellers-section">
          <div className="container">
            <div className="home-section-heading">
              <h2>{t("home.bestSellersTitle")}</h2>
              <p>{t("home.bestSellersSubtitle")}</p>
            </div>

            <div className="home-products-grid">
              {topProducts.slice(0, 4).map((product) => (
                <article
                  key={product.id}
                  className="home-product-card"
                  onClick={() => navigate(`/shop/${product.slug}`)}
                >
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} />
                  ) : (
                    <div className="home-product-placeholder">
                      {t("shop.noImage")}
                    </div>
                  )}

                  <div className="home-product-content">
                    <h3>{product.name}</h3>

                    <p className="home-product-rating">
                      ⭐ {Number(product.averageRating || 0).toFixed(1)} ·{" "}
                      {product.reviewsCount || 0} {t("shop.reviews")}
                    </p>

                    {/* {Number(product.salesCount || 0) > 0 && (
                      <p className="home-product-sales">
                        🔥 {product.salesCount} {t("shop.sales")}
                      </p>
                    )} */}

                    <strong>{Number(product.price).toFixed(2)} €</strong>

                    <button type="button">
                      {t("shop.viewProduct")}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section section-light">
        <div className="container two-columns">
          <div>
            <h2>{t("home.purposeTitle")}</h2>
            <p>{t("home.purposeText")}</p>

            <button
              className="secondary-home-btn"
              onClick={() => navigate("/contact")}
            >
              {t("home.contactCta")}
            </button>
          </div>

          <div>
            <img src={stove2Img} alt={t("home.purposeTitle")} />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="home-section-heading">
            <h2>{t("home.offerTitle")}</h2>
            <p>{t("home.offerSubtitle")}</p>
          </div>

          <div className="cards">
            <div className="card">
              <img src={componentsImg} alt={t("home.offer1Title")} />
              <h3>{t("home.offer1Title")}</h3>
              <p>{t("home.offer1Text")}</p>
            </div>

            <div className="card">
              <img src={adviceImg} alt={t("home.offer2Title")} />
              <h3>{t("home.offer2Title")}</h3>
              <p>{t("home.offer2Text")}</p>
            </div>

            <div className="card">
              <img src={europeImg} alt={t("home.offer3Title")} />
              <h3>{t("home.offer3Title")}</h3>
              <p>{t("home.offer3Text")}</p>
            </div>

            <div className="card">
              <img src={stoveImg} alt={t("home.offer4Title")} />
              <h3>{t("home.offer4Title")}</h3>
              <p>{t("home.offer4Text")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-light why-section">
        <div className="container">
          <div className="home-section-heading">
            <h2>{t("home.whyTitle")}</h2>
            <p>{t("home.whySubtitle")}</p>
          </div>

          <div className="why-grid">
            <article className="why-card">
              <FaTools className="why-icon" />
              <h3>{t("home.why1Title")}</h3>
              <p>{t("home.why1Text")}</p>
            </article>

            <article className="why-card">
              <FaHeadset className="why-icon" />
              <h3>{t("home.why2Title")}</h3>
              <p>{t("home.why2Text")}</p>
            </article>

            <article className="why-card">
              <FaTruck className="why-icon" />
              <h3>{t("home.why3Title")}</h3>
              <p>{t("home.why3Text")}</p>
            </article>

            <article className="why-card">
              <FaShieldAlt className="why-icon" />
              <h3>{t("home.why4Title")}</h3>
              <p>{t("home.why4Text")}</p>
            </article>
          </div>
        </div>
      </section>

      <section className="section trust-section">
        <div className="container center">
          <h2>{t("home.trustTitle")}</h2>

          <div className="stats">
            <div className="stat-item">
              <FaLeaf className="stat-icon" />
              <h3>{t("home.stat1Number")}</h3>
              <p>{t("home.stat1")}</p>
            </div>

            <div className="stat-item">
              <FaCertificate className="stat-icon" />
              <h3>{t("home.stat2Number")}</h3>
              <p>{t("home.stat2")}</p>
            </div>

            <div className="stat-item">
              <FaBolt className="stat-icon" />
              <h3>{t("home.stat3Number")}</h3>
              <p>{t("home.stat3")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section biomass-section">
        <div className="container">
          <h2 className="center">{t("home.biomassTitle")}</h2>
          <p className="center biomass-subtitle">{t("home.biomassSubtitle")}</p>

          <p className="center biomass-intro">
            {t("home.biomassIntro")}
          </p>

          <div className="biomass-grid">
            <div className="biomass-card">
              <img src={pelletImg} alt={t("home.pelletTitle")} />
              <FaLeaf className="biomass-icon" color="#e67e22" />
              <h3>{t("home.pelletTitle")}</h3>
              <p>{t("home.pelletText")}</p>
            </div>

            <div className="biomass-card">
              <img src={firewoodImg} alt={t("home.firewoodTitle")} />
              <FaTree className="biomass-icon" color="#e67e22" />
              <h3>{t("home.firewoodTitle")}</h3>
              <p>{t("home.firewoodText")}</p>
            </div>

            <div className="biomass-card featured">
              <span className="featured-badge">
                {t("home.featuredFuel")}
              </span>
              <img src={oliveImg} alt={t("home.oliveTitle")} />
              <GiOlive className="biomass-icon" color="#e67e22" />
              <h3>{t("home.oliveTitle")}</h3>
              <p>{t("home.oliveText")}</p>
            </div>

            <div className="biomass-card">
              <img src={biofuelImg} alt={t("home.biofuelTitle")} />
              <FaSeedling className="biomass-icon" color="#e67e22" />
              <h3>{t("home.biofuelTitle")}</h3>
              <p>{t("home.biofuelText")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container center">
          <h2>{t("home.ctaFinal")}</h2>
          <p className="cta-text">{t("home.ctaFinalText")}</p>

          <button
            className="primary-btn"
            id="final-cta"
            onClick={() => navigate("/contact")}
          >
            {t("home.contactCta")}
          </button>
        </div>
      </section>
    </>
  );
}